import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * DS-standard month navigator with prev/next circular icon buttons.
 *
 * State shape: `YYYY-MM` string (e.g. "2026-04").
 * Callers using Date objects or separate year+month numbers should
 * convert at the boundary.
 *
 * @param {string}   month       – Current month as "YYYY-MM"
 * @param {function} onChange     – Called with the new "YYYY-MM" string
 * @param {'admin'|'employee'} [theme='admin']
 * @param {string}   [minMonth]  – Earliest navigable month ("YYYY-MM"); prev disabled when reached
 * @param {string}   [maxMonth]  – Latest navigable month ("YYYY-MM"); next disabled when reached
 * @param {'default'|'compact'} [size='default'] – Label sizing
 * @param {boolean} [spreadOnMobile=false] – On small screens, pin prev/next to row edges with label centred
 * @param {string}   [className] – Extra classes on the outer wrapper
 * @param {string}   [ariaLabel] – Accessible group label
 */
export default function MonthNavigator({
  month,
  onChange,
  theme = 'admin',
  minMonth,
  maxMonth,
  size = 'default',
  spreadOnMobile = false,
  className = '',
  ariaLabel = 'Month navigation',
}) {
  const d = useMemo(() => {
    if (!month) return new Date();
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1);
  }, [month]);

  const label = useMemo(
    () => d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' }),
    [d],
  );

  const prevDisabled = minMonth ? month <= minMonth : false;
  const nextDisabled = maxMonth ? month >= maxMonth : false;

  const shift = (direction) => {
    const nd = new Date(d.getFullYear(), d.getMonth() + direction, 1);
    const value = `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}`;
    onChange(value);
  };

  const btnClass = theme === 'admin' ? 'icon-button-admin' : 'icon-button';
  const labelClass = size === 'compact'
    ? 'text-sm font-semibold text-gray-900 min-w-[140px] text-center'
    : 'text-lg font-bold text-gray-900 min-w-[180px] text-center';
  const spreadLabelClass = size === 'compact'
    ? 'text-sm font-semibold text-gray-900 text-center flex-1 sm:flex-none sm:min-w-[140px]'
    : 'text-base sm:text-lg font-bold text-gray-900 text-center flex-1 sm:flex-none sm:min-w-[180px]';

  return (
    <div
      className={`flex items-center gap-1 ${
        spreadOnMobile ? 'w-full justify-between sm:w-auto sm:justify-start' : ''
      } ${className}`}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        onClick={() => shift(-1)}
        disabled={prevDisabled}
        className={`${btnClass} flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed`}
        title="Previous month"
        aria-label="Previous month"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className={spreadOnMobile ? spreadLabelClass : labelClass}>{label}</span>
      <button
        onClick={() => shift(1)}
        disabled={nextDisabled}
        className={`${btnClass} flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed`}
        title="Next month"
        aria-label="Next month"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
